import * as React from "react";
import { useEffect } from "react";
import { Media } from "@/services/bedrock";
import { getValidFiles } from "@/utils";
import { MediaItem } from "../MediaItem";
import { Button } from "@/components/ui";
import { Plus } from "lucide-react";

interface MediaUploaderProps {
  media: Media | undefined;
  onUpdateMedia: (newMedia: Media | undefined, file: File | undefined) => void;
  isCardStyle?: boolean;
}

const MediaUploaderFC: React.FC<MediaUploaderProps> = ({ media, onUpdateMedia, isCardStyle = false }) => {
  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!media && hiddenFileInput.current) {
      hiddenFileInput.current.value = "";
    }
  }, [media]);

  const handleBrowseButtonClicked = (): void => {
    hiddenFileInput.current?.click();
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputFiles = e.target.files || new FileList();
    const validFiles = getValidFiles(Array.from(inputFiles));

    if (validFiles.length === 0) {
      return;
    }
    const file = validFiles[0];
    const mediaCategory = file.type.includes("image/") ? "IMAGE" : "VIDEO";

    const newMedia = {
      sortOrder: 0,
      category: mediaCategory,
      uri: URL.createObjectURL(file),
      type: file.type,
      extension: file.type.split("/").pop(),
    } as Media;

    onUpdateMedia(newMedia, file);
  };

  const handleDeletedMedia = (): void => {
    onUpdateMedia(undefined, undefined);
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = "";
    }
  };

  if (isCardStyle) {
    return (
      <div className="w-[130px] h-[130px] shrink-0 flex items-stretch">
        <input
          className="hidden"
          type="file"
          accept="image/*, video/*"
          onChange={handleFilesSelected}
          ref={hiddenFileInput}
        />
        <button
          type="button"
          onClick={handleBrowseButtonClicked}
          className="w-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-primary/60 hover:bg-primary/[0.02] rounded-xl p-2.5 transition-all duration-150 group/upload cursor-pointer select-none text-center"
        >
          <div className="p-1.5 rounded-full bg-muted group-hover/upload:bg-primary/5 transition-colors">
            <Plus className="size-4 text-muted-foreground group-hover/upload:text-primary transition-colors" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground group-hover/upload:text-primary transition-colors">
            Add Logo
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[50px]">
      <div className="flex flex-col items-start w-full pb-4">
        <input
          className="hidden"
          type="file"
          accept="image/*, video/*"
          onChange={handleFilesSelected}
          ref={hiddenFileInput}
        />
        {media && (
          <div className="flex flex-wrap pb-4">
            <MediaItem src={media} id={0} handleRemove={handleDeletedMedia} />
          </div>
        )}
        <Button variant="outline" type="button" onClick={handleBrowseButtonClicked}>
          {media ? "Replace File" : "Add File"}
        </Button>
      </div>
    </div>
  );
};

export const MediaUploader = MediaUploaderFC;
